import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import {
  AlertSchema,
  type Alert,
  type AlertKind,
  type AlertSeverity,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

export type AlertStatusFilter = 'PENDING' | 'ACKNOWLEDGED' | 'ALL';

export type AlertsState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; alerts: Alert[] };

/**
 * Listener real-time em /aggregations/alerts/items/. Os três filtros
 * (kind, severity, status) são client-side: a cardinalidade é baixa e, com a
 * TTL de 90 dias sobre os alertas não-HIGH (#620), fica limitada por
 * construção.
 *
 * Default da página: status=PENDING (acknowledgedAt == null). O Firestore não
 * tem operador "field is null", então status nunca poderia ser server-side.
 *
 * ## Por que `kind` também não vai como `where` (#620)
 *
 * Ia, e estava errado dos dois lados ao mesmo tempo. O código trazia
 * `if (kinds.length > 0 && kinds.length < 7)` — sete era o número de kinds
 * quando isto foi escrito, e hoje o enum tem nove. Com 7 ou 8 selecionados o
 * `where` era omitido e NÃO havia filtro client-side de kind para substituí-lo:
 * o filtro sumia em silêncio e a página mostrava tipos que o usuário tinha
 * desmarcado.
 *
 * E com 1 a 6 selecionados era pior: o índice `kind+observedAt` existe só em
 * escopo COLLECTION_GROUP (conferido em produção com
 * `gcloud firestore indexes composite list`), e esta é uma query de COLLECTION
 * — ou seja, caía em `failed-precondition` e a tela inteira ia para ERROR.
 *
 * Filtrar no cliente corrige os dois de uma vez e não precisa de índice novo.
 * Trocar o `7` por `9` teria recriado o mesmo bug no décimo kind e deixado o
 * `failed-precondition` de pé.
 *
 * Origem: sem issue — commit d4e0990a9, 2026-05-04 (rota /dashboard/alerts,
 * Sprint 4.13).
 *
 * Contrato: filtros → `AlertsState`, ao vivo (`onSnapshot`). Doc fora do
 * `AlertSchema` é pulado e reportado (`admin_alert_parse_failed`); erro do
 * listener → `ERROR` + `admin_alerts_snapshot_failed`.
 *
 * Usado por: `AlertsPage.tsx` (rota `/dashboard/alerts`; web `apps/admin`,
 * super-admin).
 *
 * Arquitetura: só leitura (o reconhecimento é do `AcknowledgeButton`).
 * Coleção direta, não collection group: `match
 * /aggregations/alerts/items/{alertId}` de `firebase/firestore.rules`
 * (`isSuperAdmin()`); `orderBy('observedAt')` sozinho usa o índice
 * automático. Escritores: `aggregations/persistAlert.ts` /
 * `detectAnomalies.ts` e `lib/tenantAlerts.ts` (Functions).
 *
 * Dados (anonimizado; `AlertSchema` em `packages/types/src/aggregations.ts`):
 *   aggregations/alerts/items/{alertId}
 *     { "alertId": "alert-1", "kind": "SALES_DROP", "severity": "HIGH",
 *       "magnitude": 0.42, "observedAt": 1759300000000,
 *       "periodAffected": { "fromYyyymmdd": "20261001", "toYyyymmdd": "20261007" },
 *       "detail": "…", "acknowledgedBy": null, "acknowledgedAt": null }
 *   — `observedAt`/`acknowledgedAt` em ms; `acknowledgedAt: null` = pendente.
 *
 * Dicas: o parágrafo acima diz que o Firestore não filtra por `null`; o SDK
 * aceita `where(campo, '==', null)` — a razão real para o filtro de status
 * ficar no cliente é não precisar de índice composto com `observedAt`
 * (registrado na #926).
 *
 * Issues: #620 (filtro de kind e TTL), #926.
 */
export function useAlerts(input: {
  kinds: readonly AlertKind[];
  severities: readonly AlertSeverity[];
  status: AlertStatusFilter;
}): AlertsState {
  const [state, setState] = useState<AlertsState>({ kind: 'LOADING' });

  // Deps estáveis: arrays novas a cada render do caller (parseList) viravam
  // re-subscribe infinito do onSnapshot — ataque de DDoS contra o próprio
  // Firestore. Serializa para string e usa como dep única.
  const kindsKey = [...input.kinds].sort().join(',');
  const severitiesKey = [...input.severities].sort().join(',');
  const statusKey = input.status;

  useEffect(() => {
    setState({ kind: 'LOADING' });
    const kinds = kindsKey === '' ? [] : (kindsKey.split(',') as AlertKind[]);
    const severities = severitiesKey === '' ? [] : (severitiesKey.split(',') as AlertSeverity[]);
    const ref = collection(firestore, 'aggregations', 'alerts', 'items');
    const unsubscribe = onSnapshot(
      query(ref, orderBy('observedAt', 'desc')),
      (snap) => {
        const alerts: Alert[] = [];
        for (const d of snap.docs) {
          const parsed = AlertSchema.safeParse(d.data());
          if (parsed.success) alerts.push(parsed.data);
          else
            reportNonFatal('admin_alert_parse_failed', {
              alertId: d.id,
              issuesSample: parsed.error.issues.slice(0, 5),
              ...serializeError(parsed.error),
            });
        }
        const filtered = alerts.filter((a) => {
          if (kinds.length > 0 && !kinds.includes(a.kind)) return false;
          if (severities.length > 0 && !severities.includes(a.severity)) {
            return false;
          }
          if (statusKey === 'PENDING' && a.acknowledgedAt !== null) return false;
          if (statusKey === 'ACKNOWLEDGED' && a.acknowledgedAt === null) return false;
          return true;
        });
        setState({ kind: 'READY', alerts: filtered });
      },
      (err) => {
        reportNonFatal('admin_alerts_snapshot_failed', serializeError(err));
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      },
    );
    return (): void => {
      unsubscribe();
    };
  }, [kindsKey, severitiesKey, statusKey]);

  return state;
}
